import { useState } from 'react'
import without from 'lodash/without'

import BellOutlinedIcon from '@ant-design/icons/BellOutlined'
import LinkOutlinedIcon from '@ant-design/icons/LinkOutlined'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormHelperText from '@mui/material/FormHelperText'
import FormLabel from '@mui/material/FormLabel'
import Paper from '@mui/material/Paper'
import Popover from '@mui/material/Popover'
import Stack from '@mui/material/Stack'

import { isUserAllowed, useMe } from '@shared/providers/src/MeProvider'
import { UserRole, userRoleToLabel } from '@shared/utils'

import CopyToClipboard from '@components/CopyToClipboard'

import { useSOPNotificationCreation } from '../SOPsDialog.hooks'

export default function DocumentActions({ data }) {
  const me = useMe()

  const canNotify = isUserAllowed(me, [UserRole.Admin, UserRole.Support])

  return (
    <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
      {canNotify && <NotifyButton data={data} />}
      <CopyToClipboard>
        {({ copy }) => (
          <Button
            color="secondary"
            startIcon={<LinkOutlinedIcon />}
            onClick={() => {
              const to = encodeURIComponent(`${import.meta.env.VITE_URL}/app?sopId=${data.id}&sops=view`)
              const url = `${import.meta.env.VITE_URL}/redirect?to=${to}`
              return copy(url)
            }}
            data-testid="sop-copy-link"
          >
            Copy link
          </Button>
        )}
      </CopyToClipboard>
    </Stack>
  )
}

function NotifyButton({ data }) {
  const [anchorEl, setAnchorEl] = useState(null)
  const onClose = () => setAnchorEl(null)

  const create = useSOPNotificationCreation(data.id)

  return (
    <>
      <Button color="secondary" startIcon={<BellOutlinedIcon />} onClick={(e) => setAnchorEl(e.currentTarget)} data-testid="sop-notify">
        Notify
      </Button>
      <Popover
        id="sop-notifcation-popover"
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <SOPNotificationForm onClose={onClose} onSubmit={create.mutate} />
      </Popover>
    </>
  )
}

const notificationRoles = without(Object.values(UserRole), UserRole.Patient, UserRole.CBO, UserRole.Pharmacy, UserRole.PharmacyManager)

function SOPNotificationForm({ onClose, onSubmit }) {
  const [roles, setRoles] = useState([])

  const handleSubmit = () => {
    onSubmit({ roles })
    onClose()
  }

  return (
    <Paper sx={{ p: 2, width: 400 }}>
      <Stack spacing={2}>
        <FormControl>
          <FormLabel id="roles-filter">Roles</FormLabel>
          <Box sx={{ mt: 1, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }} aria-labelledby="roles-filter">
            {notificationRoles.map((role) => (
              <FormControlLabel
                key={role}
                label={userRoleToLabel[role]}
                control={
                  <Checkbox
                    checked={roles.includes(role)}
                    onChange={(e, checked) => {
                      setRoles((current = []) => (checked ? [...current, role] : current.filter((v) => v !== role)))
                    }}
                  />
                }
              />
            ))}
          </Box>
          <FormHelperText>Users with these roles will receive a notification and must acknowledge the SOP update</FormHelperText>
        </FormControl>
        <Stack direction="row" spacing={1}>
          <Button size="small" fullWidth color="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button size="small" fullWidth variant="contained" onClick={handleSubmit} disabled={roles.length === 0}>
            Notify
          </Button>
        </Stack>
      </Stack>
    </Paper>
  )
}
